(() => {
  const initUpload = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const fileInput = root.querySelector('.product-upload__input');
    const status = root.querySelector('.product-upload__status');
    const keyInput = root.querySelector('input[name="properties[_upload_key]"]');
    const nameInput = root.querySelector('input[name="properties[Uploaded file]"]');
    const refInput = root.querySelector('input[name="properties[Upload ref]"]');
    const rightsCheckbox = root.querySelector('.product-upload__rights');

    const previewWrap = root.querySelector('.product-upload__preview');
    const previewImage = root.querySelector('.product-upload__preview-image');
    const previewFile = root.querySelector('.product-upload__preview-file');
    const previewFileName = root.querySelector('.product-upload__preview-file-name');

    const formId = root.dataset.productFormId;
    const form =
      document.getElementById(formId) ||
      root.closest('product-form')?.querySelector('form') ||
      root.closest('form');

    if (!form) {
      console.error('Product upload: form not found', { formId, root });
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');

    const workerUrl = (root.dataset.workerUrl || '').replace(/\/$/, '');
    const maxSize = Number(root.dataset.maxSize || 0);
    const allowedTypes = (root.dataset.allowedTypes || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const requiredUpload = root.dataset.required === 'true';

    let uploadInProgress = false;
    let lastUploadedFingerprint = '';
    let previewUrl = '';

    const setStatus = (message, type = '') => {
      status.textContent = message;
      status.className = `product-upload__status ${type}`.trim();
    };

    const setSubmitting = (disabled) => {
      if (submitButton) submitButton.disabled = disabled;
    };

    const clearPreviewUrl = () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrl = '';
      }
    };

    const hidePreview = () => {
      clearPreviewUrl();

      if (previewWrap) previewWrap.hidden = true;
      if (previewImage) {
        previewImage.hidden = true;
        previewImage.removeAttribute('src');
      }
      if (previewFile) previewFile.hidden = true;
      if (previewFileName) previewFileName.textContent = '';
    };

    const showPreview = (file) => {
      if (!previewWrap || !file) return;

      hidePreview();
      previewWrap.hidden = false;

      const type = (file.type || '').toLowerCase();
      const name = file.name || '';

      const isImage =
        type.startsWith('image/') &&
        type !== 'image/svg+xml'
          ? true
          : ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].some((ext) =>
              name.toLowerCase().endsWith(ext)
            );

      const isPdf =
        type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');

      if (isImage && previewImage) {
        previewUrl = URL.createObjectURL(file);
        previewImage.src = previewUrl;
        previewImage.hidden = false;
        return;
      }

      if (isPdf && previewFile && previewFileName) {
        previewFileName.textContent = name;
        previewFile.hidden = false;
        return;
      }

      if (previewFile && previewFileName) {
        previewFileName.textContent = name;
        previewFile.hidden = false;
      }
    };

    const resetUploadFields = () => {
      if (keyInput) keyInput.value = '';
      if (nameInput) nameInput.value = '';
      if (refInput) refInput.value = '';
      lastUploadedFingerprint = '';
    };

    const getFileFingerprint = (file) => {
      if (!file) return '';
      return [file.name, file.size, file.lastModified, file.type].join('|');
    };

    const validateFile = (file) => {
      if (!file) {
        if (requiredUpload) return 'Please choose a file before adding to cart.';
        return null;
      }

      if (maxSize && file.size > maxSize) {
        return `File is too large. Maximum size is ${Math.round(maxSize / 1048576)}MB.`;
      }

      if (allowedTypes.length > 0) {
        const fileName = file.name.toLowerCase();
        const mime = (file.type || '').toLowerCase();
        const matches = allowedTypes.some((type) => {
          if (type.startsWith('.')) return fileName.endsWith(type);
          return mime === type;
        });

        if (!matches) {
          return `Invalid file type. Allowed: ${allowedTypes.join(', ')}`;
        }
      }

      return null;
    };

    const uploadSelectedFile = async () => {
      const file = fileInput?.files?.[0];

      if (!file) {
        if (requiredUpload) {
          setStatus('Please choose a file before adding to cart.', 'error');
        } else {
          setStatus('');
        }
        hidePreview();
        return;
      }

      const validationError = validateFile(file);
      if (validationError) {
        setStatus(validationError, 'error');
        return;
      }

      if (rightsCheckbox && !rightsCheckbox.checked) {
        setStatus('Please confirm you have the rights to use this artwork.', 'error');
        return;
      }

      if (!workerUrl) {
        setStatus('Upload service is not configured.', 'error');
        return;
      }

      const fingerprint = getFileFingerprint(file);

      if (keyInput?.value && lastUploadedFingerprint === fingerprint) {
        setStatus(`Uploaded: ${file.name}${refInput?.value ? ` (${refInput.value})` : ''}`, 'success');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('originalName', file.name);
      formData.append('contentType', file.type || 'application/octet-stream');
      formData.append('productHandle', window.location.pathname.split('/').pop() || 'unknown-product');

      uploadInProgress = true;
      setSubmitting(true);
      setStatus('Uploading file...');

      try {
        const response = await fetch(`${workerUrl}/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'omit'
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
          throw new Error(data.error || 'Upload failed');
        }

        if (keyInput) keyInput.value = data.key;
        if (nameInput) nameInput.value = file.name;
        if (refInput) refInput.value = data.uploadRef || '';

        lastUploadedFingerprint = fingerprint;
        setStatus(`Uploaded: ${file.name}${data.uploadRef ? ` (${data.uploadRef})` : ''}`, 'success');
      } catch (error) {
        console.error(error);
        resetUploadFields();
        setStatus(error.message || 'Upload failed. Please try again.', 'error');
      } finally {
        uploadInProgress = false;
        setSubmitting(false);
      }
    };

    fileInput?.addEventListener('change', async () => {
      resetUploadFields();

      const file = fileInput.files?.[0];
      if (!file) {
        setStatus('');
        hidePreview();
        return;
      }

      showPreview(file);

      const validationError = validateFile(file);
      if (validationError) {
        setStatus(validationError, 'error');
        fileInput.value = '';
        hidePreview();
        return;
      }

      if (rightsCheckbox && !rightsCheckbox.checked) {
        setStatus('Please confirm you have the rights to use this artwork.', 'error');
        return;
      }

      await uploadSelectedFile();
    });

    rightsCheckbox?.addEventListener('change', async () => {
      const file = fileInput?.files?.[0];

      if (!file) {
        return;
      }

      if (rightsCheckbox.checked) {
        await uploadSelectedFile();
      } else {
        resetUploadFields();
        setStatus('Please confirm you have the rights to use this artwork.', 'error');
      }
    });

    form.addEventListener('submit', (event) => {
      const file = fileInput?.files?.[0];

      if (rightsCheckbox && !rightsCheckbox.checked && file) {
        event.preventDefault();
        setStatus('Please confirm you have the rights to use this artwork.', 'error');
        return;
      }

      if (requiredUpload && !(keyInput && keyInput.value)) {
        event.preventDefault();
        if (uploadInProgress) {
          setStatus('Please wait for the file upload to finish.', 'error');
        } else if (file) {
          setStatus('Please complete the upload before adding to cart.', 'error');
        } else {
          setStatus('Please upload a file before adding to cart.', 'error');
        }
      }
    });
  };

  const run = () => {
    document.querySelectorAll('[data-upload-root]').forEach(initUpload);
  };

  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('shopify:section:load', run);
})();